// ============================================================
// systems/auth.js — Authentication (email + password, Discord OAuth)
// The modal reuses three visual modes:
//   • signin  — email + password (+ display_name on signup)
//   • reset   — email only, triggers resetPasswordForEmail
//   • newpw   — new password, triggered by PASSWORD_RECOVERY event
// ============================================================
import { supabase } from './supabase.js';
import { state } from '../state.js';
import { dom } from '../dom.js';
import { loadGame, saveGame } from './save.js';

// ---- Main-menu auth area DOM refs ----
const authGuest         = document.getElementById('auth-guest');
const authProfile       = document.getElementById('auth-profile');
const authAvatar        = document.getElementById('auth-avatar');
const authUsername      = document.getElementById('auth-username');
const openAuthModalBtn  = document.getElementById('open-auth-modal');
const authSignout       = document.getElementById('auth-signout');

// ---- Modal DOM refs (sign-in form is the canonical one) ----
const authModalEl       = document.getElementById('auth-modal');
const authModalInner    = authModalEl.querySelector('.auth-modal');
const authModalMessage  = document.getElementById('auth-modal-message');
const authEmailForm     = document.getElementById('auth-email-form');
const authEmail         = document.getElementById('auth-email');
const authPassword      = document.getElementById('auth-password');
const signupEmail       = document.getElementById('signup-email');
const authError         = document.getElementById('auth-error');
const signinDiscord     = document.getElementById('signin-discord');
const authSkip          = document.getElementById('auth-skip');

// Skip callback for the current open — set by showAuthModal()
let _onSkip = null;
// Tracks whether we entered the recovery flow so we don't load saves
// while the user is sitting in the newpw form.
let _inRecoveryFlow = false;

// ============================================================
// Public API
// ============================================================

export function isLoggedIn() {
  return !!state.supabaseUser;
}

/**
 * Show the auth modal in sign-in mode.
 * @param {string} message   Text above the form.
 * @param {Function} [onSkip] Called when the user closes/skips.
 */
export function showAuthModal(message, onSkip) {
  authModalMessage.textContent = message;
  clearErrors();
  authEmail.value = '';
  authPassword.value = '';
  dom.authDisplayName.value = '';
  _onSkip = onSkip || null;
  setMode('signin');
  authModalInner.classList.remove('auth-signup-mode');
  authPassword.setAttribute('autocomplete', 'current-password');
  authModalEl.style.display = '';
}

export function hideAuthModal() {
  authModalEl.style.display = 'none';
  _onSkip = null;
  clearErrors();
}

// ============================================================
// Internal helpers
// ============================================================

function setMode(mode) {
  // 'signin' | 'reset' | 'newpw'
  authModalInner.setAttribute('data-mode', mode);
}

function clearErrors() {
  authError.textContent = '';
  authError.style.color = '';
  dom.authResetMsg.textContent = '';
  dom.authResetMsg.style.color = '';
  dom.authNewpwMsg.textContent = '';
  dom.authNewpwMsg.style.color = '';
}

/**
 * Derive a display name without ever leaking the raw email address.
 * Leaderboard/profile display go through this, so anything that ends
 * up in the UI or in Supabase is sanitized to at-worst the local-part
 * of the email, never the full address.
 */
function displayNameFor(profile, user) {
  const meta = user?.user_metadata || {};
  const fromMeta = meta.display_name || meta.full_name || meta.name || meta.user_name;
  const derived  = user?.email ? user.email.split('@')[0] : 'Player';
  return profile?.display_name || profile?.username || fromMeta || derived;
}

function showProfile(user, profile) {
  state.supabaseUser = user;
  state.userProfile = profile;
  state.onlineMode = true;

  authGuest.style.display = 'none';
  authProfile.style.display = '';
  authUsername.textContent = displayNameFor(profile, user);

  const avatar = profile?.avatar_url || user.user_metadata?.avatar_url || '';
  authAvatar.src = avatar;
  authAvatar.style.display = avatar ? '' : 'none';

  hideAuthModal();
}

function showGuest() {
  state.supabaseUser = null;
  state.userProfile = null;
  state.onlineMode = false;

  authGuest.style.display = '';
  authProfile.style.display = 'none';
}

async function fetchProfile(userId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    if (error) console.warn('[auth] profile fetch error:', error.message);
    return data || null;
  } catch (e) {
    console.warn('[auth] profile fetch threw:', e?.message || e);
    return null;
  }
}

async function upsertProfile(userId, displayName) {
  try {
    const payload = { id: userId };
    if (displayName) payload.display_name = displayName;
    const { error } = await supabase.from('profiles').upsert(payload);
    if (error) console.warn('[auth] profile upsert error:', error.message);
  } catch (e) {
    console.warn('[auth] profile upsert threw:', e?.message || e);
  }
}

function withBusy(btn, promise) {
  if (!btn) return promise;
  btn.disabled = true;
  return Promise.resolve(promise).finally(() => { btn.disabled = false; });
}

function validateEmail(errEl, email) {
  errEl.style.color = '';
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errEl.textContent = 'Please enter a valid email address.';
    return false;
  }
  errEl.textContent = '';
  return true;
}

function validatePassword(errEl, password) {
  errEl.style.color = '';
  if (!password || password.length < 8) {
    errEl.textContent = 'Password must be at least 8 characters.';
    return false;
  }
  errEl.textContent = '';
  return true;
}

// Humanize common Supabase auth errors
function friendlyAuthError(error) {
  const msg = (error?.message || '').toLowerCase();
  if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
    return 'Please confirm your email first — check your inbox.';
  }
  if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
    return 'Email or password is incorrect.';
  }
  if (msg.includes('already registered') || msg.includes('already been registered')) {
    return 'That email already has an account — try signing in.';
  }
  if (msg.includes('rate limit') || msg.includes('too many')) {
    return 'Too many attempts — please wait a minute and try again.';
  }
  return error?.message || 'Something went wrong.';
}

// ============================================================
// Init
// ============================================================

export async function initAuth() {
  // Restore existing session if any
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const profile = await fetchProfile(session.user.id);
      showProfile(session.user, profile);
    } else {
      showGuest();
    }
  } catch (e) {
    console.warn('[auth] getSession threw:', e?.message || e);
    showGuest();
  }

  // --- Auth state transitions ---
  supabase.auth.onAuthStateChange(async (event, newSession) => {
    if (event === 'PASSWORD_RECOVERY') {
      _inRecoveryFlow = true;
      authModalMessage.textContent = 'Choose a new password for your account.';
      dom.authNewpw.value = '';
      setMode('newpw');
      authModalEl.style.display = '';
      return;
    }

    if (event === 'SIGNED_IN' && newSession?.user && !_inRecoveryFlow) {
      const profile = await fetchProfile(newSession.user.id);
      showProfile(newSession.user, profile);
      // Pull cloud save (or keep local if cloud is empty), then save —
      // that migrates any guest progress up to the cloud on first login.
      try {
        await loadGame();
        saveGame();
      } catch (err) {
        console.warn('[auth] post-login sync failed:', err?.message || err);
      }
      return;
    }

    if (event === 'USER_UPDATED' && newSession?.user) {
      _inRecoveryFlow = false;
      const profile = await fetchProfile(newSession.user.id);
      state.userProfile = profile;
      authUsername.textContent = displayNameFor(profile, newSession.user);
      return;
    }

    if (event === 'SIGNED_OUT') {
      _inRecoveryFlow = false;
      showGuest();
      return;
    }
  });

  // --- "Sign In" button on main menu ---
  openAuthModalBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    showAuthModal('Sign in to save your progress and compete on the leaderboard!');
  });

  // --- Modal close X ---
  dom.authModalCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const cb = _onSkip;
    hideAuthModal();
    if (cb) cb();
  });

  // --- Sign-in form submit ---
  authEmailForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const email = authEmail.value.trim();
    const password = authPassword.value;
    if (!validateEmail(authError, email)) return;
    if (!validatePassword(authError, password)) return;

    await withBusy(dom.authSubmitSignin, (async () => {
      try {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) authError.textContent = friendlyAuthError(error);
      } catch (err) {
        authError.textContent = 'Network error — please try again.';
        console.warn('[auth] signIn threw:', err?.message || err);
      }
    })());
  });

  // --- Sign up: first click reveals display-name input, second click submits ---
  signupEmail.addEventListener('click', async (e) => {
    e.stopPropagation();
    if (!authModalInner.classList.contains('auth-signup-mode')) {
      authModalInner.classList.add('auth-signup-mode');
      authPassword.setAttribute('autocomplete', 'new-password');
      authError.style.color = '#ffcc80';
      authError.textContent = 'Pick a display name (3-24 chars), then click Sign up again.';
      dom.authDisplayName.focus();
      return;
    }
    const email = authEmail.value.trim();
    const password = authPassword.value;
    const displayName = dom.authDisplayName.value.trim();
    if (!validateEmail(authError, email)) return;
    if (!validatePassword(authError, password)) return;
    if (displayName.length < 3 || displayName.length > 24) {
      authError.style.color = '';
      authError.textContent = 'Display name must be 3–24 characters.';
      return;
    }
    await withBusy(signupEmail, (async () => {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: displayName } }
        });
        if (error) {
          authError.textContent = friendlyAuthError(error);
          return;
        }
        // If the project auto-signs-in on sign-up (email confirmation
        // disabled), upsert the profile now so the name is present
        // before anything else reads it. When confirmation IS required
        // there is no session yet, so we rely on the handle_new_user
        // trigger (0001_rls_policies.sql) instead.
        if (data?.session?.user?.id) {
          await upsertProfile(data.session.user.id, displayName);
        }

        authError.style.color = '#69db7c';
        authError.textContent = data?.session
          ? 'Account created!'
          : 'Account created — check your email to confirm, then sign in.';
        authModalInner.classList.remove('auth-signup-mode');
        authPassword.setAttribute('autocomplete', 'current-password');
      } catch (err) {
        authError.textContent = 'Network error — please try again.';
        console.warn('[auth] signUp threw:', err?.message || err);
      }
    })());
  });

  // --- Forgot password link → reset mode ---
  dom.authForgotBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    clearErrors();
    dom.authResetEmail.value = authEmail.value.trim();
    setMode('reset');
  });

  dom.authResetCancel.addEventListener('click', (e) => {
    e.stopPropagation();
    clearErrors();
    setMode('signin');
  });

  dom.authResetForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const email = dom.authResetEmail.value.trim();
    if (!validateEmail(dom.authResetMsg, email)) return;

    await withBusy(dom.authSubmitReset, (async () => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + window.location.pathname,
        });
        if (error) {
          dom.authResetMsg.textContent = friendlyAuthError(error);
        } else {
          // Intentionally non-committal so we don't confirm whether an
          // account exists (account-enumeration defense).
          dom.authResetMsg.style.color = '#69db7c';
          dom.authResetMsg.textContent = 'If that email has an account, a reset link is on its way.';
        }
      } catch (err) {
        dom.authResetMsg.textContent = 'Network error — please try again.';
        console.warn('[auth] resetPasswordForEmail threw:', err?.message || err);
      }
    })());
  });

  // --- New password submission (after PASSWORD_RECOVERY event) ---
  dom.authNewpwForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pw = dom.authNewpw.value;
    if (!validatePassword(dom.authNewpwMsg, pw)) return;

    await withBusy(dom.authSubmitNewpw, (async () => {
      try {
        const { error } = await supabase.auth.updateUser({ password: pw });
        if (error) {
          dom.authNewpwMsg.textContent = friendlyAuthError(error);
        } else {
          dom.authNewpwMsg.style.color = '#69db7c';
          dom.authNewpwMsg.textContent = 'Password updated!';
          _inRecoveryFlow = false;
          setTimeout(() => hideAuthModal(), 1200);
        }
      } catch (err) {
        dom.authNewpwMsg.textContent = 'Network error — please try again.';
        console.warn('[auth] updateUser threw:', err?.message || err);
      }
    })());
  });

  // --- Discord OAuth ---
  signinDiscord.addEventListener('click', async (e) => {
    e.stopPropagation();
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: { redirectTo: window.location.origin + window.location.pathname }
      });
    } catch (err) {
      authError.textContent = 'Could not start Discord sign-in.';
      console.warn('[auth] discord signin threw:', err?.message || err);
    }
  });

  // --- Guest skip ---
  authSkip.addEventListener('click', (e) => {
    e.stopPropagation();
    const cb = _onSkip;
    hideAuthModal();
    if (cb) cb();
  });

  // --- Backdrop click ---
  authModalEl.addEventListener('click', (e) => {
    if (e.target !== authModalEl) return;
    // Block backdrop-close during the forced newpw flow — the user
    // must pick a password before escaping the recovery state.
    if (authModalInner.getAttribute('data-mode') === 'newpw') return;
    const cb = _onSkip;
    hideAuthModal();
    if (cb) cb();
  });

  // --- Sign out ---
  authSignout.addEventListener('click', async (e) => {
    e.stopPropagation();
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.warn('[auth] signOut threw:', err?.message || err);
    }
    showGuest();
  });
}
